import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { PlaceItem } from "@/types/place";

export enum ESidebarMenuType {
  SPATIAL = "spatial",
  DATASET = "dataset",
  DATASET_EXPLORER = "data_explorer",
  SEARCH = "search",
  ANALYSIS_HISTORY = "analysis_history",
  AGENTIC_AI = "agentic_ai"
}

export type SidebarMenuTypePopup =
  | ESidebarMenuType.SPATIAL
  | ESidebarMenuType.DATASET
  | ESidebarMenuType.DATASET_EXPLORER
  | ESidebarMenuType.SEARCH
  | ESidebarMenuType.ANALYSIS_HISTORY
  | ESidebarMenuType.AGENTIC_AI
  | null;

export interface ComparisonPopupState {
  isShow: boolean;
  /* Panels that were open when comparison mode was entered. Comparison resets
    the whole ui slice, so these flags carry the state that must be restored
    when the user leaves comparison mode. */
  isFromAnalysisResult: boolean;
  isFromAnalysisSummary: boolean;
  isLimitShow: boolean;
}

export interface SpatialState {
  allowPinMarker?: boolean;
  showAreaAdminPopup?: boolean;
  showPolygonPopup?: boolean;
  showCatchmentPopup?: boolean;
  showDatasetPopup?: boolean;
  showAnalysisSummaryPopup: boolean;
  showAnalysisResultPopup?: boolean;
  showAnalysisJobList?: boolean;
  showDataWeightSettingsPopup?: boolean;
  showDatasetExplorerPopup?: boolean;
  showSearchLocationPopup?: boolean;
  showGlobalFilterPopup?: boolean;
  markerInfo?: PlaceItem | string | null;
  showTemplateModal?: boolean;
  showSpatialAnalysisResultTabularModal: boolean;
  showComparisonPopup: ComparisonPopupState;
  sideBarMenuTypePopupDisplay?: SidebarMenuTypePopup;
  isExpandLegendVisualization?: boolean;
  showProjectList?: boolean;
  sidebarUserSettingPopupDisplay: {
    isPanelView: boolean;
    isPanelAdd: boolean;
    isPanelEdit: boolean;
  };
  isDrawPolygon?: boolean;
  isCustomDrawPolygonDataset?: boolean;
  showDrawLimitInfo?: boolean;
  sidebarMenu: boolean;
  showLegendInfo?: boolean;
  showLoadingOverlay: boolean;
  showPanelDatasetDetail?: {
    isAddNewRow: boolean;
    isEditRow: boolean;
  };
  showNpsModal: boolean;
  npsMilestoneReached: string | null;
  npsId?: string;
  showTimeSeriesModal: boolean;
  showDatasetWidgetPopup: boolean;
  showProductOverviewPanel: boolean;
  /** Collapses the read-only Analysis Config panel to its header bar, which
   * also moves the Analysis Result panel underneath it instead of beside it. */
  isAnalysisConfigMinimized: boolean;
}

export const uiInitialState: SpatialState = {
  allowPinMarker: false,
  showAreaAdminPopup: false,
  showCatchmentPopup: false,
  showDatasetPopup: false,
  showPolygonPopup: false,
  showAnalysisResultPopup: false,
  showAnalysisSummaryPopup: false,
  showSearchLocationPopup: false,
  showGlobalFilterPopup: false,
  markerInfo: null,
  showDataWeightSettingsPopup: false,
  showSpatialAnalysisResultTabularModal: false,
  showTemplateModal: false,
  showComparisonPopup: {
    isShow: false,
    isFromAnalysisResult: false,
    isFromAnalysisSummary: false,
    isLimitShow: false
  },
  sideBarMenuTypePopupDisplay: null,
  isExpandLegendVisualization: false,
  showProjectList: false,
  sidebarUserSettingPopupDisplay: {
    isPanelView: false,
    isPanelAdd: false,
    isPanelEdit: false
  },
  sidebarMenu: false,
  showLegendInfo: false,
  isDrawPolygon: false,
  showDrawLimitInfo: false,
  showLoadingOverlay: false,
  showAnalysisJobList: false,
  showPanelDatasetDetail: {
    isAddNewRow: false,
    isEditRow: false
  },
  isCustomDrawPolygonDataset: false,
  showNpsModal: false,
  npsMilestoneReached: null,
  showTimeSeriesModal: false,
  showDatasetWidgetPopup: false,
  showProductOverviewPanel: false,
  isAnalysisConfigMinimized: false
};

export const uiSlice = createSlice({
  name: "ui",
  initialState: uiInitialState,
  reducers: {
    toggleAllowPinMarker: (state, action: PayloadAction<boolean>) => {
      state.allowPinMarker = action.payload;
    },

    toggleAreaAdminPopup: (state, action: PayloadAction<boolean>) => {
      state.showAreaAdminPopup = action.payload;
    },
    toggleCatchmentPopup: (state, action: PayloadAction<boolean>) => {
      state.showCatchmentPopup = action.payload;
    },
    toggleDatasetExplorerPopup: (state, action: PayloadAction<boolean>) => {
      state.showDatasetExplorerPopup = action.payload;
    },
    toggleDataWeightSettingsPopup: (state, action: PayloadAction<boolean>) => {
      state.showDataWeightSettingsPopup = action.payload;
    },
    toggleDatasetPopup: (state, action: PayloadAction<boolean>) => {
      state.showDatasetPopup = action.payload;
    },
    toggleAnalysisResultPopup: (state) => {
      state.showAnalysisResultPopup = !state.showAnalysisResultPopup;
      if (!state.showAnalysisResultPopup) {
        state.isAnalysisConfigMinimized = false;
      }
    },
    toggleComparisonLimitShow: (state) => {
      state.showComparisonPopup.isLimitShow =
        !state.showComparisonPopup.isLimitShow;
    },
    toggleLegendInfo: (state) => {
      state.showLegendInfo = !state.showLegendInfo;
    },
    toggleLegendVisualization: (state) => {
      state.isExpandLegendVisualization = !state.isExpandLegendVisualization;
    },
    toggleShowProjectList: (state, action: PayloadAction<boolean>) => {
      state.showProjectList = action.payload;
    },
    toggleSpatialAnalysisResultTabularModal: (state) => {
      state.showSpatialAnalysisResultTabularModal =
        !state.showSpatialAnalysisResultTabularModal;
    },
    toggleAnalysisJobList: (state) => {
      state.showAnalysisJobList = !state.showAnalysisJobList;
    },
    toggleGlobalFilterPopup: (state, action: PayloadAction<boolean>) => {
      state.showGlobalFilterPopup = action.payload;
    },
    setShowTemplateModal: (state, action: PayloadAction<boolean>) => {
      state.showTemplateModal = action.payload;
    },
    setShowAnalysisSummaryPopup: (state, action: PayloadAction<boolean>) => {
      state.showAnalysisSummaryPopup = action.payload;
    },
    setShowDrawLimitInfo: (state, action: PayloadAction<boolean>) => {
      state.showDrawLimitInfo = action.payload;
    },
    setAnalysisResultPopup: (state, action: PayloadAction<boolean>) => {
      state.showAnalysisResultPopup = action.payload;
      if (!action.payload) {
        state.isAnalysisConfigMinimized = false;
      }
    },
    /* Reset alongside the result popup so every new analysis opens expanded —
       the minimized state describes one viewing session, not a preference. */
    setAnalysisConfigMinimized: (state, action: PayloadAction<boolean>) => {
      state.isAnalysisConfigMinimized = action.payload;
    },
    setActiveSpatialAnalysisPanel: (state, action: PayloadAction<string>) => {
      const activePanel = action.payload;

      state.showCatchmentPopup = activePanel === "showCatchmentPopup";
      state.showAreaAdminPopup = activePanel === "showAreaAdminPopup";
      state.showPolygonPopup = activePanel === "showPolygonPopup";
      state.showDataWeightSettingsPopup =
        activePanel === "showDataWeightSettingsPopup";
      state.showDatasetExplorerPopup =
        activePanel === "showDatasetExplorerPopup";
      state.showDatasetPopup = activePanel === "showDatasetPopup";
      state.showGlobalFilterPopup = activePanel === "showGlobalFilterPopup";
    },
    setSideBarMenuTypePopupDisplay: (
      state,
      action: PayloadAction<SidebarMenuTypePopup>
    ) => {
      state.sideBarMenuTypePopupDisplay = action.payload;
    },
    setShowComparisonPopup: (
      state,
      action: PayloadAction<ComparisonPopupState>
    ) => {
      state.showComparisonPopup = action.payload;
    },
    disableDrawPolygon: (_state, action: PayloadAction<boolean>) => {
      return {
        ...uiInitialState,
        sideBarMenuTypePopupDisplay: ESidebarMenuType.SPATIAL,
        showPolygonPopup: !action.payload,
        isDrawPolygon: false,
        showCatchmentPopup: action.payload
      };
    },
    enableDrawPolygon: () => {
      return {
        ...uiInitialState,
        isDrawPolygon: true
      };
    },
    setShowPolygonPopup: (state, action: PayloadAction<boolean>) => {
      state.showPolygonPopup = action.payload;
    },
    setSidebarUserSettingPopupDisplay: (
      state,
      action: PayloadAction<{
        isPanelView: boolean;
        isPanelAdd: boolean;
        isPanelEdit: boolean;
      }>
    ) => {
      state.sidebarUserSettingPopupDisplay = action.payload;
    },
    resetSidebarUserSettingPopupDisplay: (state) => {
      state.sidebarUserSettingPopupDisplay = {
        isPanelView: false,
        isPanelAdd: false,
        isPanelEdit: false
      };
    },
    toggleSidebarMenu: (state, action: PayloadAction<boolean>) => {
      state.sidebarMenu = action.payload;
    },
    setUIDataConfig: (state, action: PayloadAction<SpatialState>) => {
      return {
        ...state,
        ...action.payload
      };
    },
    resetUIDataConfig: () => uiInitialState,
    resetUIDataConfigExceptSidebarMenu: (state) => {
      return {
        ...uiInitialState,
        sidebarMenu: state.sidebarMenu
      };
    },
    toggleShowSearchLocationPopup: (state) => {
      state.showSearchLocationPopup = !state.showSearchLocationPopup;
    },
    setMarkerInfo: (
      state,
      action: PayloadAction<PlaceItem | string | null>
    ) => {
      state.markerInfo = action.payload;
    },
    setLoadingOverlay: (state, action: PayloadAction<boolean>) => {
      state.showLoadingOverlay = action.payload;
    },
    resetShowPanelDatasetDetail: (state) => {
      state.showPanelDatasetDetail = {
        isAddNewRow: false,
        isEditRow: false
      };
    },
    setShowPanelDatasetDetail: (
      state,
      action: PayloadAction<{
        isAddNewRow: boolean;
        isEditRow: boolean;
      }>
    ) => {
      state.showPanelDatasetDetail = action.payload;
    },
    enabledCustomDrawPolygonDataset: (state) => {
      state.isCustomDrawPolygonDataset = true;
    },
    disabledCustomDrawPolygonDataset: (state) => {
      state.isCustomDrawPolygonDataset = false;
    },
    setShowNpsModal: (state, action: PayloadAction<boolean>) => {
      state.showNpsModal = action.payload;
    },
    setNpsMilestoneReached: (state, action: PayloadAction<string | null>) => {
      state.npsMilestoneReached = action.payload;
    },
    setNpsId: (state, action: PayloadAction<string>) => {
      state.npsId = action.payload;
    },
    setShowTimeSeriesModal: (state, action: PayloadAction<boolean>) => {
      state.showTimeSeriesModal = action.payload;
    },
    toggleDatasetWidgetPopup: (state) => {
      state.showDatasetWidgetPopup = !state.showDatasetWidgetPopup;
    },
    setShowProductOverviewPanel: (state, action: PayloadAction<boolean>) => {
      state.showProductOverviewPanel = action.payload;
    }
  }
});

export const {
  toggleAllowPinMarker,
  toggleAreaAdminPopup,
  toggleCatchmentPopup,
  toggleDatasetPopup,
  toggleDataWeightSettingsPopup,
  toggleSpatialAnalysisResultTabularModal,
  toggleComparisonLimitShow,
  toggleAnalysisResultPopup,
  toggleDatasetExplorerPopup,
  toggleLegendVisualization,
  toggleShowProjectList,
  toggleLegendInfo,
  toggleSidebarMenu,
  toggleAnalysisJobList,
  toggleGlobalFilterPopup,
  resetSidebarUserSettingPopupDisplay,
  resetUIDataConfig,
  resetUIDataConfigExceptSidebarMenu,
  setUIDataConfig,
  setShowPolygonPopup,
  setShowComparisonPopup,
  setSidebarUserSettingPopupDisplay,
  setActiveSpatialAnalysisPanel,
  setSideBarMenuTypePopupDisplay,
  setShowAnalysisSummaryPopup,
  setShowDrawLimitInfo,
  setAnalysisResultPopup,
  setAnalysisConfigMinimized,
  toggleShowSearchLocationPopup,
  setMarkerInfo,
  enableDrawPolygon,
  disableDrawPolygon,
  setShowTemplateModal,
  setLoadingOverlay,
  resetShowPanelDatasetDetail,
  setShowPanelDatasetDetail,
  enabledCustomDrawPolygonDataset,
  disabledCustomDrawPolygonDataset,
  setShowNpsModal,
  setNpsMilestoneReached,
  setNpsId,
  setShowTimeSeriesModal,
  toggleDatasetWidgetPopup,
  setShowProductOverviewPanel
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;
